import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    Paper,
    Skeleton,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useEffect, useState } from 'react';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';
import CommunicationManagementService, {
    CommunicationChannel,
    RecipientType,
    Template,
    TemplateVariant,
} from '../../services/CommunicationManagementService';

const service = new CommunicationManagementService();
const STEPS = ['Select Channel & Template', 'Fill Variables', 'Review & Send'];

const CHANNEL_CONFIG = [
    {
        value: CommunicationChannel.SMS,
        label: 'SMS',
        description: 'Send a text message',
        icon: <SmsIcon sx={{ fontSize: 26 }} />,
        bg: '#dbeafe',
        color: '#1d4ed8',
        border: '#93c5fd',
    },
    {
        value: CommunicationChannel.WHATSAPP,
        label: 'WhatsApp',
        description: 'Send via WhatsApp',
        icon: <WhatsAppIcon sx={{ fontSize: 26 }} />,
        bg: '#dcfce7',
        color: '#15803d',
        border: '#86efac',
    },
    {
        value: CommunicationChannel.FCM,
        label: 'In-App',
        description: 'Push notification',
        icon: <NotificationsActiveIcon sx={{ fontSize: 26 }} />,
        bg: '#f3e8ff',
        color: '#7e22ce',
        border: '#d8b4fe',
    },
];

interface Props {
    open: boolean;
    onClose: () => void;
    customerId: number;
    customerName?: string;
}

export default function SendCommunicationDialog({ open, onClose, customerId, customerName }: Props) {
    const [activeStep, setActiveStep] = useState(0);

    const [channel, setChannel] = useState<CommunicationChannel>(CommunicationChannel.SMS);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [templateSearch, setTemplateSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [selectedVariant, setSelectedVariant] = useState<TemplateVariant | null>(null);

    const [variables, setVariables] = useState<Record<string, string>>({});
    const [preview, setPreview] = useState('');
    const [loadingPreview, setLoadingPreview] = useState(false);

    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelectedTemplate(null);
        setSelectedVariant(null);
        setTemplates([]);
        setTemplateSearch('');
        setLoadingTemplates(true);
        service
            .listTemplates(channel)
            .then(setTemplates)
            .catch(() => EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Failed to load templates',
                type: CustomSnackbarTypes.ERROR,
            }))
            .finally(() => setLoadingTemplates(false));
    }, [channel, open]);

    useEffect(() => {
        if (!selectedVariant) return;
        const initial: Record<string, string> = {};
        selectedVariant.variables.forEach((v) => {
            initial[v] = selectedVariant.defaultVariables?.[v] || '';
        });
        setVariables(initial);
        setPreview('');
    }, [selectedVariant]);

    const handleClose = () => { handleReset(); onClose(); };

    const handleReset = () => {
        setActiveStep(0);
        setSelectedTemplate(null);
        setSelectedVariant(null);
        setVariables({});
        setPreview('');
        setSending(false);
        setTemplateSearch('');
    };

    const handleSelectVariant = (template: Template, variant: TemplateVariant) => {
        setSelectedTemplate(template);
        setSelectedVariant(variant);
        setActiveStep(1);
    };

    const handlePreview = async () => {
        if (!selectedVariant) return;
        setLoadingPreview(true);
        try {
            const res = await service.previewMessage(channel, selectedVariant.id, variables);
            setPreview(res.preview);
        } catch {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Failed to generate preview',
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleSend = async () => {
        if (!selectedVariant) return;
        setSending(true);
        try {
            await service.sendMessage(channel, selectedVariant.id, variables, RecipientType.CUSTOMER, customerId);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Message queued successfully',
                type: CustomSnackbarTypes.SUCCESS,
            });
            EventBus.emitEvent(EventBusEventsEnum.COMMUNICATION_MESSAGE_SENT, { customerId });
            handleClose();
        } catch {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Failed to send message',
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setSending(false);
        }
    };

    const allVariablesFilled = selectedVariant
        ? selectedVariant.variables.length === 0 || selectedVariant.variables.every((v) => variables[v]?.trim())
        : false;

    const filteredRows = templates
        .flatMap((t) => t.variants.map((v) => ({ template: t, variant: v })))
        .filter(({ template }) => {
            if (!templateSearch) return true;
            const q = templateSearch.toLowerCase();
            return (
                template.name.toLowerCase().includes(q) ||
                (template.friendlyName ?? '').toLowerCase().includes(q)
            );
        });

    const selectedChannelConfig = CHANNEL_CONFIG.find((c) => c.value === channel)!;

    // ── Step 1: Channel + Template ─────────────────────────────────────────
    const renderStep1 = () => (
        <Box>
            {/* Channel cards */}
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Channel
            </Typography>
            <Box display="flex" gap={1.5} mt={1} mb={3}>
                {CHANNEL_CONFIG.map((ch) => {
                    const selected = channel === ch.value;
                    return (
                        <Box
                            key={ch.value}
                            onClick={() => setChannel(ch.value)}
                            sx={{
                                flex: 1, p: 2, borderRadius: 2, cursor: 'pointer',
                                border: `2px solid ${selected ? ch.border : 'transparent'}`,
                                bgcolor: selected ? ch.bg : alpha('#f8fafc', 1),
                                outline: selected ? `1px solid ${ch.border}` : '1px solid #e2e8f0',
                                transition: 'all 0.15s',
                                '&:hover': { bgcolor: ch.bg, outline: `1px solid ${ch.border}` },
                            }}
                        >
                            <Box sx={{ color: selected ? ch.color : 'text.secondary', mb: 0.5 }}>
                                {ch.icon}
                            </Box>
                            <Typography variant="body2" fontWeight={700} sx={{ color: selected ? ch.color : 'text.primary' }}>
                                {ch.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {ch.description}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Template search + table */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                    Template
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search templates…"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, borderRadius: 1.5 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {['Template name', 'Language', 'Variables', ''].map((h) => (
                                <TableCell key={h} sx={{
                                    fontWeight: 600, fontSize: 11, color: 'text.secondary',
                                    bgcolor: '#f8fafc', textTransform: 'uppercase', letterSpacing: 0.4,
                                }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingTemplates ? (
                            [...Array(4)].map((_, i) => (
                                <TableRow key={i}>
                                    {[200, 80, 120, 70].map((w, j) => (
                                        <TableCell key={j}><Skeleton width={w} height={18} /></TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                    No templates found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRows.map(({ template: t, variant: v }) => {
                                const isSelected = selectedVariant?.id === v.id;
                                return (
                                    <TableRow key={`${t.id}-${v.id}`} hover
                                        sx={{
                                            bgcolor: isSelected ? alpha(selectedChannelConfig.bg, 0.5) : undefined,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleSelectVariant(t, v)}
                                    >
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            <Tooltip title={t.name} placement="top-start">
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontWeight: 500 }}>
                                                    {t.friendlyName || t.name}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={v.language || 'EN'} size="small"
                                                sx={{ fontSize: 11, height: 20, bgcolor: 'grey.100' }} />
                                        </TableCell>
                                        <TableCell>
                                            {v.variables.length > 0
                                                ? <Box display="flex" gap={0.5} flexWrap="wrap">
                                                    {v.variables.slice(0, 3).map((vr) => (
                                                        <Chip key={vr} label={vr} size="small"
                                                            sx={{ fontSize: 10, height: 18, bgcolor: alpha(selectedChannelConfig.bg, 0.6), color: selectedChannelConfig.color }} />
                                                    ))}
                                                    {v.variables.length > 3 && <Chip label={`+${v.variables.length - 3}`} size="small" sx={{ fontSize: 10, height: 18 }} />}
                                                  </Box>
                                                : <Typography variant="caption" color="text.disabled">None</Typography>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {isSelected
                                                ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: selectedChannelConfig.color }} />
                                                : <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>Select →</Typography>
                                            }
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    // ── Step 2: Variables + Preview ────────────────────────────────────────
    const renderStep2 = () => (
        <Box>
            {/* Selected template banner */}
            <Paper variant="outlined" sx={{
                p: 1.5, mb: 3, borderRadius: 2,
                bgcolor: alpha(selectedChannelConfig.bg, 0.4),
                borderColor: selectedChannelConfig.border,
                display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
                <Box sx={{ color: selectedChannelConfig.color }}>{selectedChannelConfig.icon}</Box>
                <Box>
                    <Typography variant="body2" fontWeight={700}>
                        {selectedTemplate?.friendlyName || selectedTemplate?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {selectedChannelConfig.label} · {selectedVariant?.language}
                    </Typography>
                </Box>
                <Button size="small" sx={{ ml: 'auto', fontSize: 11 }} onClick={() => setActiveStep(0)}>
                    Change
                </Button>
            </Paper>

            <Box display="flex" gap={2.5}>
                {/* Left: template text + buttons reference */}
                <Box flex={1} minWidth={0}>
                    {selectedVariant?.templateText && (
                        <Box mb={2}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Template Content
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.75, borderRadius: 1.5, bgcolor: '#f8fafc' }}>
                                <Typography variant="body2" whiteSpace="pre-wrap" sx={{ lineHeight: 1.6, fontSize: 12.5 }}>
                                    {selectedVariant.templateText}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                    {selectedVariant?.buttons && selectedVariant.buttons.length > 0 && (
                        <Box>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Buttons (predefined)
                            </Typography>
                            <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.75}>
                                {selectedVariant.buttons.map((btn) => (
                                    <Chip key={btn} label={btn} size="small" variant="outlined"
                                        sx={{ fontSize: 11, color: selectedChannelConfig.color, borderColor: selectedChannelConfig.border }} />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Right: variable inputs */}
                {selectedVariant && selectedVariant.variables.length > 0 && (
                    <Box flex={1} minWidth={0}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Fill in variables
                        </Typography>

                        {selectedTemplate?.sampleMessage && (
                            <Paper variant="outlined" sx={{
                                mt: 0.75, mb: 1.5, p: 1.25, borderRadius: 1.5,
                                bgcolor: '#fffbeb', borderColor: '#fcd34d',
                            }}>
                                <Typography variant="caption" fontWeight={700} color="#92400e"
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', mb: 0.25 }}>
                                    Example
                                </Typography>
                                <Typography variant="caption" color="#78350f" whiteSpace="pre-wrap" sx={{ lineHeight: 1.5 }}>
                                    {selectedTemplate.sampleMessage}
                                </Typography>
                            </Paper>
                        )}

                        <Box display="flex" flexDirection="column" gap={1.5} mt={0.75}>
                            {selectedVariant.variables.map((key) => (
                                <TextField
                                    key={key}
                                    label={key}
                                    value={variables[key] || ''}
                                    onChange={(e) => setVariables((p) => ({ ...p, [key]: e.target.value }))}
                                    fullWidth
                                    size="small"
                                    multiline={key === 'body' || key === 'message'}
                                    rows={key === 'body' || key === 'message' ? 3 : 1}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Preview */}
            <Box mt={2.5}>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handlePreview}
                        disabled={!allVariablesFilled || loadingPreview}
                        sx={{ borderRadius: 1.5, fontSize: 12 }}
                        startIcon={loadingPreview ? <CircularProgress size={12} /> : undefined}
                    >
                        {loadingPreview ? 'Generating…' : 'Preview message'}
                    </Button>
                    {!allVariablesFilled && selectedVariant && selectedVariant.variables.length > 0 && (
                        <Typography variant="caption" color="text.disabled">Fill all variables to preview</Typography>
                    )}
                </Box>
                {preview && (
                    <Paper variant="outlined" sx={{
                        p: 2, mt: 1.5, borderRadius: 1.5,
                        borderColor: '#86efac', bgcolor: '#f0fdf4',
                    }}>
                        <Typography variant="caption" fontWeight={600} color="success.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Preview
                        </Typography>
                        <Typography variant="body2" mt={0.75} whiteSpace="pre-wrap" sx={{ lineHeight: 1.6 }}>
                            {preview}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Box>
    );

    // ── Step 3: Review + Send ──────────────────────────────────────────────
    const renderStep3 = () => (
        <Box>
            {/* Summary card */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2.5 }}>
                {/* Header stripe */}
                <Box sx={{ bgcolor: alpha(selectedChannelConfig.bg, 0.7), px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: selectedChannelConfig.color }}>{selectedChannelConfig.icon}</Box>
                    <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: selectedChannelConfig.color }}>
                            {selectedChannelConfig.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Ready to send</Typography>
                    </Box>
                </Box>
                <Divider />

                {/* Details grid */}
                <Box sx={{ px: 2.5, py: 2 }}>
                    {[
                        { label: 'Template', value: selectedTemplate?.friendlyName || selectedTemplate?.name },
                        { label: 'Language', value: selectedVariant?.language },
                    ].map(({ label, value }) => (
                        <Box key={label} display="flex" alignItems="flex-start" gap={1} mb={1.25}>
                            <Typography variant="body2" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>{label}</Typography>
                            <Typography variant="body2" fontWeight={600}>{value}</Typography>
                        </Box>
                    ))}

                    <Box display="flex" alignItems="flex-start" gap={1} mb={1.25}>
                        <Typography variant="body2" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>Recipient</Typography>
                        <Box display="flex" alignItems="center" gap={0.75}>
                            <PersonOutlineIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                            <Typography variant="body2" fontWeight={600}>
                                {customerName || `Customer #${customerId}`}
                            </Typography>
                            <Chip label={`ID ${customerId}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'grey.100' }} />
                        </Box>
                    </Box>

                    {/* Filled variables */}
                    {Object.keys(variables).length > 0 && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Variables
                            </Typography>
                            <Box mt={1} display="flex" flexDirection="column" gap={0.75}>
                                {Object.entries(variables).map(([k, v]) => (
                                    <Box key={k} display="flex" gap={1}>
                                        <Typography variant="caption" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>{k}</Typography>
                                        <Typography variant="caption" fontWeight={500} sx={{
                                            bgcolor: alpha(selectedChannelConfig.bg, 0.5),
                                            color: selectedChannelConfig.color,
                                            px: 0.75, py: 0.25, borderRadius: 0.75,
                                            wordBreak: 'break-all',
                                        }}>
                                            {v || <em style={{ opacity: 0.5 }}>empty</em>}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}

                    {/* Preview */}
                    {preview && (
                        <>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                Message Preview
                            </Typography>
                            <Typography variant="body2" mt={0.75} whiteSpace="pre-wrap" sx={{ lineHeight: 1.6, fontSize: 12.5 }}>
                                {preview}
                            </Typography>
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    );

    const stepContent = [renderStep1(), renderStep2(), renderStep3()];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2.5, overflow: 'hidden' } }}>

            {/* Title */}
            <DialogTitle sx={{
                px: 3, py: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderBottom: '1px solid', borderColor: 'divider',
                bgcolor: '#fafafa',
            }}>
                <Box>
                    <Typography variant="h6" fontWeight={700} fontSize={16}>
                        Send Communication
                    </Typography>
                    {customerName && (
                        <Typography variant="caption" color="text.secondary">
                            To: {customerName} · ID {customerId}
                        </Typography>
                    )}
                </Box>
                <IconButton size="small" onClick={handleClose} sx={{ color: 'text.secondary' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* Stepper */}
            <Box sx={{ px: 3, pt: 2.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STEPS.map((label, i) => (
                        <Step key={label} completed={activeStep > i}>
                            <StepLabel
                                sx={{
                                    '& .MuiStepLabel-label': { fontSize: 12, fontWeight: activeStep === i ? 700 : 400 },
                                }}
                            >
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Content */}
            <DialogContent sx={{ px: 3, py: 2.5, minHeight: 320 }}>
                {stepContent[activeStep]}
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa', gap: 1 }}>
                {activeStep > 0 && (
                    <Button variant="outlined" size="small" onClick={() => setActiveStep((s) => s - 1)}
                        disabled={sending} sx={{ borderRadius: 1.5 }}>
                        Back
                    </Button>
                )}
                <Box flex={1} />
                {activeStep === 0 && (
                    <Button variant="contained" size="small" disableElevation
                        onClick={() => setActiveStep(1)}
                        disabled={!selectedVariant}
                        sx={{ borderRadius: 1.5, px: 2.5 }}>
                        Continue
                    </Button>
                )}
                {activeStep === 1 && (
                    <Button variant="contained" size="small" disableElevation
                        onClick={() => setActiveStep(2)}
                        disabled={!allVariablesFilled}
                        sx={{ borderRadius: 1.5, px: 2.5 }}>
                        Review
                    </Button>
                )}
                {activeStep === 2 && (
                    <Button
                        variant="contained"
                        size="small"
                        disableElevation
                        color="success"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={13} color="inherit" /> : <SendIcon fontSize="small" />}
                        sx={{ borderRadius: 1.5, px: 2.5, fontWeight: 700 }}
                    >
                        {sending ? 'Sending…' : 'Send now'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
